import { createSlice } from "@reduxjs/toolkit";
import { getRestaurantStorageItem, setRestaurantStorageItem } from "../utils/restaurantStorageUtils";

const UI_STORAGE_KEY = "online_ordering_ui";

const loadUiState = (restaurantSlug) => {
  if (!restaurantSlug) {
    return {
      isCartOpen: false,
      collectionTimeModalOpen: false,
      browseStartOrderModalOpen: false,
      /** Collect from home: wait for time confirm before opening menu */
      pendingCollectionMenuEntry: false,
      /** Browse menu: product clicked before Delivery/Collection is chosen */
      pendingBrowseProduct: null,
      globalSearchQuery: "",
    };
  }

  try {
    const stored = getRestaurantStorageItem(restaurantSlug, UI_STORAGE_KEY);
    if (stored && typeof stored === "object") {
      return {
        isCartOpen: !!stored.isCartOpen,
        collectionTimeModalOpen: false,
        browseStartOrderModalOpen: false,
        pendingCollectionMenuEntry: false,
        pendingBrowseProduct: null,
        globalSearchQuery: "",
      };
    }
  } catch (error) {
    console.error("Error loading online ordering UI state:", error);
  }

  return {
    isCartOpen: false,
    collectionTimeModalOpen: false,
    browseStartOrderModalOpen: false,
    pendingCollectionMenuEntry: false,
    pendingBrowseProduct: null,
    globalSearchQuery: "",
  };
};

const saveUiState = (restaurantSlug, uiState) => {
  if (!restaurantSlug) return;
  try {
    setRestaurantStorageItem(restaurantSlug, UI_STORAGE_KEY, {
      isCartOpen: !!uiState.isCartOpen,
    });
  } catch (error) {
    console.error("Error saving online ordering UI state:", error);
  }
};

const initialState = {
  states: {},
  currentRestaurantSlug: null,
};

const onlineOrderingUiSlice = createSlice({
  name: "onlineOrderingUi",
  initialState,
  reducers: {
    syncUiFromStorage: (state, action) => {
      const { restaurantSlug } = action.payload || {};
      if (!restaurantSlug) return;
      state.states[restaurantSlug] = loadUiState(restaurantSlug);
      state.currentRestaurantSlug = restaurantSlug;
    },
    setCartOpen: (state, action) => {
      const { restaurantSlug, isCartOpen } = action.payload || {};
      if (!restaurantSlug) return;
      if (!state.states[restaurantSlug]) {
        state.states[restaurantSlug] = loadUiState(restaurantSlug);
      }
      state.states[restaurantSlug].isCartOpen = !!isCartOpen;
      state.currentRestaurantSlug = restaurantSlug;
      saveUiState(restaurantSlug, state.states[restaurantSlug]);
    },
    toggleCartOpen: (state, action) => {
      const { restaurantSlug } = action.payload || {};
      if (!restaurantSlug) return;
      if (!state.states[restaurantSlug]) {
        state.states[restaurantSlug] = loadUiState(restaurantSlug);
      }
      state.states[restaurantSlug].isCartOpen = !state.states[restaurantSlug].isCartOpen;
      state.currentRestaurantSlug = restaurantSlug;
      saveUiState(restaurantSlug, state.states[restaurantSlug]);
    },
    setCollectionTimeModalOpen: (state, action) => {
      const { restaurantSlug, isOpen } = action.payload || {};
      if (!restaurantSlug) return;
      if (!state.states[restaurantSlug]) {
        state.states[restaurantSlug] = loadUiState(restaurantSlug);
      }
      state.states[restaurantSlug].collectionTimeModalOpen = !!isOpen;
      state.currentRestaurantSlug = restaurantSlug;
    },
    setPendingCollectionMenuEntry: (state, action) => {
      const { restaurantSlug, pending } = action.payload || {};
      if (!restaurantSlug) return;
      if (!state.states[restaurantSlug]) {
        state.states[restaurantSlug] = loadUiState(restaurantSlug);
      }
      state.states[restaurantSlug].pendingCollectionMenuEntry = !!pending;
      state.currentRestaurantSlug = restaurantSlug;
    },
    setBrowseStartOrderModalOpen: (state, action) => {
      const { restaurantSlug, isOpen } = action.payload || {};
      if (!restaurantSlug) return;
      if (!state.states[restaurantSlug]) {
        state.states[restaurantSlug] = loadUiState(restaurantSlug);
      }
      state.states[restaurantSlug].browseStartOrderModalOpen = !!isOpen;
      state.currentRestaurantSlug = restaurantSlug;
    },
    setPendingBrowseProduct: (state, action) => {
      const { restaurantSlug, product } = action.payload || {};
      if (!restaurantSlug) return;
      if (!state.states[restaurantSlug]) {
        state.states[restaurantSlug] = loadUiState(restaurantSlug);
      }
      state.states[restaurantSlug].pendingBrowseProduct =
        product && typeof product === "object" ? product : null;
      state.currentRestaurantSlug = restaurantSlug;
    },
    clearPendingBrowseProduct: (state, action) => {
      const { restaurantSlug } = action.payload || {};
      if (!restaurantSlug) return;
      if (!state.states[restaurantSlug]) {
        state.states[restaurantSlug] = loadUiState(restaurantSlug);
      }
      state.states[restaurantSlug].pendingBrowseProduct = null;
      state.currentRestaurantSlug = restaurantSlug;
    },
    setGlobalSearchQuery: (state, action) => {
      const { restaurantSlug, query } = action.payload || {};
      if (!restaurantSlug) return;
      if (!state.states[restaurantSlug]) {
        state.states[restaurantSlug] = loadUiState(restaurantSlug);
      }
      state.states[restaurantSlug].globalSearchQuery =
        typeof query === "string" ? query : "";
      state.currentRestaurantSlug = restaurantSlug;
    },
    clearGlobalSearchQuery: (state, action) => {
      const { restaurantSlug } = action.payload || {};
      if (!restaurantSlug) return;
      if (!state.states[restaurantSlug]) {
        state.states[restaurantSlug] = loadUiState(restaurantSlug);
      }
      state.states[restaurantSlug].globalSearchQuery = "";
      state.currentRestaurantSlug = restaurantSlug;
    },
  },
});

export const {
  syncUiFromStorage,
  setCartOpen,
  toggleCartOpen,
  setCollectionTimeModalOpen,
  setPendingCollectionMenuEntry,
  setBrowseStartOrderModalOpen,
  setPendingBrowseProduct,
  clearPendingBrowseProduct,
  setGlobalSearchQuery,
  clearGlobalSearchQuery,
} = onlineOrderingUiSlice.actions;

export const selectIsCartOpen = (state, restaurantSlug) => {
  if (!restaurantSlug) return false;
  return !!state.onlineOrderingUi?.states?.[restaurantSlug]?.isCartOpen;
};

export const selectCollectionTimeModalOpen = (state, restaurantSlug) => {
  if (!restaurantSlug) return false;
  return !!state.onlineOrderingUi?.states?.[restaurantSlug]?.collectionTimeModalOpen;
};

export const selectPendingCollectionMenuEntry = (state, restaurantSlug) => {
  if (!restaurantSlug) return false;
  return !!state.onlineOrderingUi?.states?.[restaurantSlug]?.pendingCollectionMenuEntry;
};

export const selectBrowseStartOrderModalOpen = (state, restaurantSlug) => {
  if (!restaurantSlug) return false;
  return !!state.onlineOrderingUi?.states?.[restaurantSlug]?.browseStartOrderModalOpen;
};

export const selectPendingBrowseProduct = (state, restaurantSlug) => {
  if (!restaurantSlug) return null;
  return state.onlineOrderingUi?.states?.[restaurantSlug]?.pendingBrowseProduct ?? null;
};

export const selectGlobalSearchQuery = (state, restaurantSlug) => {
  if (!restaurantSlug) return "";
  return state.onlineOrderingUi?.states?.[restaurantSlug]?.globalSearchQuery ?? "";
};

export default onlineOrderingUiSlice.reducer;

